import React, { useState } from 'react';
import { Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import {
  useTranslator,
  useAppContext,
  useToast,
  cloudflareImage,
  mealPlaceholderImage,
  formatCalories,
} from '@front-activerse/common-components';

import { Loader } from '@front-activerse/common-components';
import { useNavigate, useOutletContext } from 'react-router-dom';

const DayPlanMealReplacement = ({
  mealReplacement,
  replaceMeal,
  insertFavoriteToDayplan,
  originalMealId = undefined,
  mealId,
  userDietSetId,
  mealType,
  from,
}) => {
  const navigate = useNavigate();
  const { trackFirebaseEvent } = useOutletContext();
  const { t } = useTranslator();
  const { toastSuccess } = useToast();

  const [preload, setPreload] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [errorTitle, setErrorTitle] = useState('');
  const [errorDesscription, setErrorDesscription] = useState('');
  const [errorSkipable, setErrorSkipable] = useState(false);

  const {
    consts: { APP_BRAND },
    images: { changeMealIcon, favIcon, ratingStarFull },
  } = useAppContext();

  const toggleModal = () => {
    setShowAlertModal(!showAlertModal);
  };

  const changeMeal = async (skipValidation) => {
    setPreload(true);
    setShowAlertModal(false);

    let success = false;
    // let realReplacementId = mealReplacement.id;

    // meal replacement with favourite meals
    if (
      mealReplacement.isFavorited &&
      originalMealId !== mealReplacement.id &&
      originalMealId !== -1
    ) {
      const response = await insertFavoriteToDayplan.insert(
        mealReplacement.id,
        originalMealId || mealId,
        skipValidation,
        userDietSetId,
        mealType
      );
      if (response.data.me.insertFavoriteToDayplan.__typename === 'BasicMutationSuccessWithId') {
        success = true;
        // realReplacementId = response.data.me.insertFavoriteToDayplan.id;
      } else if (
        response.data.me.insertFavoriteToDayplan.__typename === 'InsertToDayPlanValidationException'
      ) {
        const { skipable, title, description } = response.data.me.insertFavoriteToDayplan;

        setErrorTitle(title);
        setErrorDesscription(description);
        setErrorSkipable(skipable);
        setShowAlertModal(true);
      }
    }
    // normal meal replacement
    else {
      const replacement = {
        originalMealId: originalMealId === -1 ? mealId : originalMealId,
        replacementId: mealReplacement.id,
        userDietSetId: userDietSetId,
        mealType: mealType,
      };

      const response = await replaceMeal.replace(replacement);

      if (response.data.me.dietSetReplaceMeal.__typename === 'BasicMutationSuccess') {
        success = true;
      } else {
        setErrorTitle(t('error/occurred'));
        setErrorDesscription(t('error/message/generic'));
        setErrorSkipable(false);
        setShowAlertModal(true);
      }
    }

    if (success) {
      setPreload(false);
      toastSuccess(<p className="mb-0">{t('mealPlan.changeMeal.confirmationNotification')}</p>);

      if (from === 'day-plan') {
        navigate(-1);
      } else {
        navigate(-2);
      }
      trackFirebaseEvent('user_action', {
        action: 'tap-change_meal',
        location: 'Change_meal',
        meal_id: mealReplacement.id,
        meal_favourites: mealReplacement.isFavorited ? 'yes' : 'no',
      });
    } else {
      setPreload(false);
    }
  };

  const renderMealImage = () => {
    let mealImg;
    if (mealReplacement.preparationImageUrl) {
      mealImg = mealReplacement.preparationImageUrl;
    } else {
      mealImg = mealPlaceholderImage(mealType, APP_BRAND);
    }

    const coverImage = cloudflareImage(mealImg, `${APP_BRAND}`, 'mealPlanThumbnail');

    return <img src={coverImage} className="img-fluid" width={80} height={80} alt="" />;
  };

  const steps = [5, 15, 30, 45, 60];

  const calculatePreparationTime = (minutes) => {
    if (minutes === 0 || minutes == null) {
      // return '0 min';
      return null;
    }

    for (const step of steps) {
      if (minutes <= step) {
        return `< ${step} min`;
      }
    }
    return `> ${steps[steps.length - 1]} min`;
  };

  const calculatedPreparationTime = calculatePreparationTime(
    mealReplacement.preparationTime.minutes
  );

  return (
    <>
      <div
        className="meal-box d-flex align-items-center cursor-pointer"
        onClick={() => changeMeal()}
      >
        <div className="meal-image">{renderMealImage()}</div>
        <div className="meal-content">
          <div className="tags">
            {mealReplacement.rating && mealReplacement.rating.averageRating && (
              <span className="tag tag-rating me-1">
                <img src={ratingStarFull} className="me-1" alt="" />
                {mealReplacement.rating.averageRating.averageRating}
              </span>
            )}
            {mealReplacement.isFavorited && (
              <span className="tag tag-favorite me-1">
                <img src={favIcon} className="me-1" alt="" />
                Favorite
              </span>
            )}
            {mealReplacement.mealCustomTag && mealReplacement.mealCustomTag.label && (
              <span
                className="tag me-1"
                style={{ backgroundColor: mealReplacement.mealCustomTag.color, color: '#fff' }}
              >
                {mealReplacement.mealCustomTag.label}
              </span>
            )}
          </div>
          <p className="small semi-bold mb-xxs">{mealReplacement.publicName}</p>

          <div className="meal-meta mt-0 mt-md-2">
            <p className="mb-0">
              <span>{formatCalories(mealReplacement.kcal)}&nbsp;kcal</span>
              {mealReplacement.preparationTime.minutes !== 0 && (
                <>
                  <strong className="bullet-separator mx-1">•</strong>
                  <span>{calculatedPreparationTime}</span>
                </>
              )}
            </p>
          </div>

          {/* <p className="description text-text-variant-color mb-0">
            {mealReplacement.dishes.map((dish) => {
              let j = 0;
              return (
                <React.Fragment key={dish.id}>
                  {dish.ingredients &&
                    dish.ingredients.length > 1 &&
                    dish.ingredients.map((ingredient) => {
                      j += 1;
                      // eslint-disable-next-line prettier/prettier
                      if (j <= 3) {
                        return (
                          <span key={ingredient.name}>
                            {j >= 2 ? ', ' : ''}
                            {ingredient.name}
                          </span>
                        );
                      }
                      return null;
                    })}
                </React.Fragment>
              );
            })}
          </p> */}
        </div>

        <img src={changeMealIcon} className="img-fluid" alt="" />
      </div>

      {preload ? <Loader /> : ''}

      <Modal isOpen={showAlertModal} toggle={toggleModal} centered size="lg" className="favourites">
        <ModalHeader toggle={toggleModal}>{errorTitle}</ModalHeader>
        <ModalBody>
          <p className="navy">{errorDesscription}</p>
        </ModalBody>
        <ModalFooter>
          {errorSkipable ? (
            <Row className="w-100">
              <Col xs="6" className="pe-2">
                <Button color="secondary" outline className="w-100" onClick={() => toggleModal()}>
                  {t('cancel')}
                </Button>
              </Col>
              <Col xs="6" className="ps-2">
                <Button color="primary" className="w-100" onClick={() => changeMeal(true)}>
                  {t('insert')}
                </Button>
              </Col>
            </Row>
          ) : (
            <Button color="secondary" outline className="w-100" onClick={() => toggleModal()}>
              {t('cancel')}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default DayPlanMealReplacement;
